/**
 * Shared fuzzy value rendering helpers.
 *
 * Extracted from IntelDetailModal.tsx (lines 352, 358, 676, 680) so both
 * IntelDetailModal and the Dossier tabs render fuzzy values consistently
 * (single source of truth at the UI layer, matching D16 at the backend layer).
 *
 * Also adds renderFuzzyValueDouble for FuzzyValueDouble DTO (used by MilitaryMath range returns).
 */

import { FuzzyValue } from '../types/intel';

/** FuzzyValueDouble — decimal low/high bounds (from MilitaryMath range computations). */
export interface FuzzyValueDouble {
  low: number;
  high: number;
}

/** Formats a number with locale-appropriate thousands separators. */
function formatNumber(n: number): string {
  return n.toLocaleString();
}

/**
 * Renders a FuzzyValue integer range as a human-readable string.
 *
 * - null → 'Unknown'
 * - low === high → single value (no range)
 * - low !== high → "low - high"
 */
export function renderFuzzyValue(value: FuzzyValue | null): string {
  if (!value) return 'Unknown';
  if (value.low === value.high) return formatNumber(value.low);
  return `${formatNumber(value.low)} - ${formatNumber(value.high)}`;
}

/**
 * Renders a FuzzyValue as a percentage string.
 *
 * Input values are expected to be in 0..1 range (e.g. 0.87 → "87.0%").
 *
 * - null → 'Unknown'
 * - low === high → single percentage
 * - low !== high → "low% - high%"
 */
export function renderFuzzyPercent(value: FuzzyValue | null): string {
  if (!value) return 'Unknown';
  if (value.low === value.high) return `${(value.low * 100).toFixed(1)}%`;
  return `${(value.low * 100).toFixed(1)}% - ${(value.high * 100).toFixed(1)}%`;
}

/**
 * Returns the midpoint of a FuzzyValue as a rounded integer.
 *
 * Used when a single representative value is needed (e.g. table sort keys).
 */
export function fuzzyMidpoint(value: FuzzyValue): number {
  return Math.round((value.low + value.high) / 2);
}

/**
 * Returns the midpoint of a FuzzyValue as an unrounded decimal.
 *
 * Used for fractional values like military efficiency.
 */
export function fuzzyMidpointDouble(value: FuzzyValue): number {
  return (value.low + value.high) / 2;
}

/**
 * Renders a FuzzyValueDouble (decimal) range as a human-readable string.
 *
 * Used for MilitaryMath range returns (modifiedOffense, modifiedDefense).
 *
 * - null → 'Unknown'
 * - low === high → single value formatted to nearest integer
 * - low !== high → "low - high" both formatted to nearest integer
 */
export function renderFuzzyValueDouble(value: FuzzyValueDouble | null): string {
  if (!value) return 'Unknown';
  const lo = Math.round(value.low);
  const hi = Math.round(value.high);
  if (lo === hi) return formatNumber(lo);
  return `${formatNumber(lo)} - ${formatNumber(hi)}`;
}
